import { useCallback, useEffect, useRef, useState } from "react";
import type { UploadedFile } from "@/components/content/uploaded-files-list";
import { captureDatadogError } from "@/lib/analytics-loader";
import {
  isUploadFailure,
  putFileWithProgress,
} from "@/lib/utils/put-file-with-progress";

type TelemetryContext = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface UseFileUploadQueueOptions {
  /** Asks the API for a signed PUT URL for this file. */
  getUploadUrl: (file: File) => Promise<string>;
  /** Names the uploader in Datadog, e.g. `content-submission`. */
  source: string;
  /** Extra Datadog context for failures, such as the organization and profile ids. */
  context?: TelemetryContext;
}

/**
 * How many times a file whose connection dropped while the page was hidden is
 * restarted on its own once the creator is back. iOS Safari drops a
 * backgrounded page's connections, which lost long video uploads (EPD-12608).
 */
export const MAX_AUTOMATIC_RESUMES = 2;

export const UPLOAD_NETWORK_ERROR_MESSAGE =
  "The connection dropped before this file finished uploading. Retry on a stronger connection; Wi-Fi works best for large videos.";
export const UPLOAD_FAILED_MESSAGE = "This file didn't upload. Please retry.";
export const UPLOAD_PAUSED_NOTICE =
  "Paused while you were away from this page. It restarts when you come back.";

const FALLBACK_CONTENT_TYPE = "application/octet-stream";

/**
 * `fetch` rejects with a TypeError only when the request got no response, and
 * each browser words it differently ("Load failed", "Failed to fetch",
 * "NetworkError when attempting to fetch resource"). tRPC wraps it in `cause`.
 */
function isFetchFailure(error: Error): boolean {
  return error instanceof TypeError || error.cause instanceof TypeError;
}

function isNetworkError(err: unknown): boolean {
  if (isUploadFailure(err)) return err.reason === "network";
  // A signed-URL request that never reached the API.
  return err instanceof Error && isFetchFailure(err);
}

function isPageHidden(): boolean {
  return document.visibilityState === "hidden";
}

/** Resolves once the page is visible, so an upload never starts in a backgrounded tab. */
function waitUntilVisible(): Promise<void> {
  if (!isPageHidden()) return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (isPageHidden()) return;
      document.removeEventListener("visibilitychange", onChange);
      resolve();
    };
    document.addEventListener("visibilitychange", onChange);
  });
}

function newUploadId(file: File): string {
  return `${Date.now()}-${file.name}-${file.size}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Uploads files to signed storage URLs one at a time (EPD-12608), so several
 * large videos never split a slow connection between them. Each file reports
 * progress, a failed file can be retried, a file whose connection dropped while
 * the page was hidden restarts when the creator comes back, and every failure is
 * reported to Datadog. Leaving the page mid-upload asks for confirmation where
 * the browser allows it.
 */
export function useFileUploadQueue({
  getUploadUrl,
  source,
  context,
}: UseFileUploadQueueOptions) {
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const queueRef = useRef<string[]>([]);
  const fileByIdRef = useRef(new Map<string, File>());
  const resumesRef = useRef(new Map<string, number>());
  const activeRef = useRef<{ id: string; controller: AbortController } | null>(
    null,
  );
  const pumpingRef = useRef(false);
  const mountedRef = useRef(false);
  const getUploadUrlRef = useRef(getUploadUrl);
  const telemetryRef = useRef({ source, context });

  useEffect(() => {
    getUploadUrlRef.current = getUploadUrl;
    telemetryRef.current = { source, context };
  });

  const update = useCallback((id: string, patch: Partial<UploadedFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }, []);

  const uploadOne = useCallback(
    async (id: string, file: File) => {
      const controller = new AbortController();
      activeRef.current = { id, controller };
      update(id, {
        status: "uploading",
        progress: 0,
        error: undefined,
        notice: undefined,
      });

      const startedAt = Date.now();
      let percent = 0;
      let wasHidden = isPageHidden();
      const onVisibility = () => {
        if (isPageHidden()) wasHidden = true;
      };
      document.addEventListener("visibilitychange", onVisibility);

      try {
        const signedUrl = await getUploadUrlRef.current(file);
        await putFileWithProgress({
          url: signedUrl,
          file,
          contentType: file.type || FALLBACK_CONTENT_TYPE,
          signal: controller.signal,
          onProgress: (loaded, total) => {
            // 100 is kept for storage's answer, not the last byte sent.
            const next =
              total > 0 ? Math.min(99, Math.floor((loaded / total) * 100)) : 0;
            if (next === percent) return;
            percent = next;
            update(id, { progress: next });
          },
        });
        resumesRef.current.delete(id);
        update(id, {
          status: "done",
          progress: 100,
          url: signedUrl.split("?")[0],
        });
      } catch (err) {
        // Removed, cleared, or unmounted: nothing to show or report.
        if (controller.signal.aborted) return;

        const network = isNetworkError(err);
        const resumes = resumesRef.current.get(id) ?? 0;
        const willResume =
          network &&
          (wasHidden || isPageHidden()) &&
          resumes < MAX_AUTOMATIC_RESUMES;

        const { source: telemetrySource, context: telemetryContext } =
          telemetryRef.current;
        void captureDatadogError("Content upload did not complete", {
          ...telemetryContext,
          source: telemetrySource,
          reason: isUploadFailure(err)
            ? err.reason
            : network
              ? "network"
              : "upload-url",
          httpStatus: isUploadFailure(err) ? err.status : undefined,
          rawError: err instanceof Error ? err.message : String(err),
          fileType: file.type || null,
          fileSizeBytes: file.size,
          progressPercent: percent,
          elapsedMs: Date.now() - startedAt,
          pageWasHidden: wasHidden,
          willResume,
          attempt: resumes + 1,
        });

        if (willResume) {
          resumesRef.current.set(id, resumes + 1);
          queueRef.current.unshift(id);
          update(id, {
            status: "queued",
            progress: 0,
            notice: UPLOAD_PAUSED_NOTICE,
          });
        } else {
          update(id, {
            status: "error",
            progress: 0,
            error: network
              ? UPLOAD_NETWORK_ERROR_MESSAGE
              : UPLOAD_FAILED_MESSAGE,
          });
        }
      } finally {
        document.removeEventListener("visibilitychange", onVisibility);
        if (activeRef.current?.id === id) activeRef.current = null;
      }
    },
    [update],
  );

  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    try {
      while (mountedRef.current && queueRef.current.length > 0) {
        await waitUntilVisible();
        const id = queueRef.current.shift();
        const file = id === undefined ? undefined : fileByIdRef.current.get(id);
        if (id === undefined || !file || !mountedRef.current) continue;
        await uploadOne(id, file);
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [uploadOne]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current = [];
      activeRef.current?.controller.abort();
    };
  }, []);

  const addFiles = useCallback(
    (selected: File[]) => {
      if (selected.length === 0) return;
      const added: UploadedFile[] = selected.map((file) => ({
        id: newUploadId(file),
        file,
        url: "",
        progress: 0,
        status: "queued",
      }));
      for (const entry of added) {
        fileByIdRef.current.set(entry.id, entry.file);
        queueRef.current.push(entry.id);
      }
      setFiles((prev) => [...prev, ...added]);
      void pump();
    },
    [pump],
  );

  const retryFile = useCallback(
    (id: string) => {
      if (
        !fileByIdRef.current.has(id) ||
        queueRef.current.includes(id) ||
        activeRef.current?.id === id
      )
        return;
      resumesRef.current.delete(id);
      queueRef.current.push(id);
      update(id, {
        status: "queued",
        progress: 0,
        error: undefined,
        notice: undefined,
      });
      void pump();
    },
    [pump, update],
  );

  const removeFile = useCallback((id: string) => {
    fileByIdRef.current.delete(id);
    resumesRef.current.delete(id);
    queueRef.current = queueRef.current.filter((queued) => queued !== id);
    if (activeRef.current?.id === id) activeRef.current.controller.abort();
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const clearFiles = useCallback(() => {
    fileByIdRef.current.clear();
    resumesRef.current.clear();
    queueRef.current = [];
    activeRef.current?.controller.abort();
    setFiles([]);
  }, []);

  const isUploading = files.some(
    (f) => f.status === "uploading" || f.status === "queued",
  );

  // Leaving mid-upload loses the file; ask first where the browser allows it.
  useEffect(() => {
    if (!isUploading) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older Chrome and Safari only prompt when returnValue is set.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isUploading]);

  return {
    files,
    addFiles,
    retryFile,
    removeFile,
    clearFiles,
    isUploading,
    hasUploadedFiles: files.some((f) => f.status === "done"),
  };
}
